import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCustomerAuth } from "../lib/customerAuth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Sparkle, Lock, ArrowLeft, CalendarBlank, Car, Receipt, ShieldCheck,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatErr } from "../lib/api";
import GlobalHeader from "../components/layout/GlobalHeader";
import PasswordInput from "./components/PasswordInput";

/**
 * Customer login — premium dark/light mode.
 * Dark:  fondo #0F1117 con cards #1A1D26 y acentos dorados #D4AF37.
 * Light: fondo #F7F6F2 con cards blancos y acentos dorados #B8860B.
 */
export default function CustomerLogin() {
  const navigate = useNavigate();
  const { login } = useCustomerAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(form.email, form.password);
      toast.success("Bienvenido de nuevo");
      navigate("/mi-cuenta");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Credenciales incorrectas");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F7F6F2] dark:bg-[#0F1117] transition-colors">
      <GlobalHeader variant="auto" testId="customer-login-header" showRegisterCta={false} />

      {/* Halo decorativo dorado */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[420px] overflow-hidden">
        <div className="absolute top-[-180px] left-1/2 -translate-x-1/2 w-[640px] h-[640px] rounded-full
          bg-[radial-gradient(closest-side,rgba(212,175,55,0.18),transparent)]
          dark:bg-[radial-gradient(closest-side,rgba(212,175,55,0.22),transparent)]" />
      </div>

      <div className="flex-1 flex items-start sm:items-center justify-center px-4 py-6 sm:py-10 relative z-10">
        <div className="w-full max-w-md">
          <Link
            to="/marketplace"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-[#B8860B]
              dark:text-gray-400 dark:hover:text-[#F5C76A] mb-5 transition-colors"
            data-testid="cl-back"
          >
            <ArrowLeft size={12} weight="bold" /> Volver al Marketplace
          </Link>

          <div
            className="rounded-2xl p-8 shadow-xl border transition-colors
              bg-white border-gray-200
              dark:bg-[#1A1D26] dark:border-white/10 dark:shadow-[0_30px_80px_-30px_rgba(0,0,0,0.7)]"
            data-testid="customer-login-card"
          >
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-3
                bg-gradient-to-br from-[#D4AF37] to-[#B8860B]
                dark:from-[#F5C76A] dark:to-[#D4AF37] shadow-lg shadow-amber-900/20">
                <Sparkle size={26} weight="fill" className="text-[#1A1A1A]" />
              </div>
              <h1 className="font-display text-3xl font-black tracking-tighter text-[#1A1A1A] dark:text-white">
                Mi cuenta
              </h1>
              <p className="text-xs mt-2 max-w-xs mx-auto leading-relaxed text-gray-500 dark:text-gray-400">
                Tus reservas, vehículos y facturas en un solo sitio.
              </p>
            </div>

            <div className="grid grid-cols-3 gap-2 mb-6 text-center" data-testid="cl-benefits">
              <Benefit Icon={CalendarBlank} label="Reservas en 1 tap" />
              <Benefit Icon={Car} label="Mis vehículos" />
              <Benefit Icon={Receipt} label="Facturas PDF" />
            </div>

            <form onSubmit={submit} className="space-y-4" data-testid="customer-login-form">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-500 dark:text-gray-400">
                  Email
                </Label>
                <Input
                  type="email" required value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1.5 h-11 rounded-lg bg-[#F7F6F2] dark:bg-[#0F1117] border-gray-200 dark:border-white/10
                    focus-visible:ring-[#D4AF37] dark:focus-visible:ring-[#F5C76A]"
                  autoComplete="email"
                  data-testid="cl-email"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-500 dark:text-gray-400">
                  Contraseña
                </Label>
                <PasswordInput
                  required value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="mt-1.5 h-11 rounded-lg bg-[#F7F6F2] dark:bg-[#0F1117] border-gray-200 dark:border-white/10
                    focus-visible:ring-[#D4AF37] dark:focus-visible:ring-[#F5C76A]"
                  autoComplete="current-password"
                  testId="cl-password"
                />
              </div>
              <Button
                type="submit" disabled={busy}
                className="w-full h-12 rounded-lg font-bold tracking-wide
                  bg-gradient-to-r from-[#D4AF37] to-[#B8860B] hover:from-[#B8860B] hover:to-[#996F0A]
                  dark:from-[#F5C76A] dark:to-[#D4AF37] dark:hover:from-[#D4AF37] dark:hover:to-[#B8860B]
                  text-[#1A1A1A] shadow-lg shadow-amber-900/20 transition-all"
                data-testid="cl-submit"
              >
                <Lock size={14} weight="bold" className="mr-1.5" />
                {busy ? "Entrando…" : "Entrar"}
              </Button>
              <div className="text-center">
                <Link
                  to="/cliente/recuperar-password"
                  className="text-xs font-semibold text-[#B8860B] hover:text-[#996F0A]
                    dark:text-[#F5C76A] dark:hover:text-[#FFD580] hover:underline"
                  data-testid="cl-forgot-link"
                >
                  ¿Has olvidado tu contraseña?
                </Link>
              </div>
            </form>

            <div className="text-center mt-6 pt-6 border-t border-gray-100 dark:border-white/10 text-xs text-gray-500 dark:text-gray-400">
              ¿Aún no tienes cuenta?{" "}
              <Link
                to="/cliente/signup"
                className="font-bold text-[#B8860B] hover:text-[#996F0A]
                  dark:text-[#F5C76A] dark:hover:text-[#FFD580] hover:underline"
                data-testid="cl-to-signup"
              >
                Crear cuenta
              </Link>
            </div>
          </div>

          {/* Trust strip */}
          <div className="mt-6 flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold
            text-[var(--ag-text-muted)] dark:text-gray-500">
            <ShieldCheck size={12} weight="duotone" />
            Conexión segura · Datos cifrados
          </div>
        </div>
      </div>
    </div>
  );
}

function Benefit({ Icon, label }) {
  return (
    <div className="rounded-xl py-3 px-1 border transition-colors
      bg-[#F7F6F2] border-gray-100
      dark:bg-[#0F1117] dark:border-white/10">
      <Icon size={18} weight="duotone" className="mx-auto mb-1.5 text-[#B8860B] dark:text-[#F5C76A]" />
      <div className="text-[9px] uppercase tracking-wider font-bold leading-tight
        text-gray-600 dark:text-gray-400">
        {label}
      </div>
    </div>
  );
}
